<template>
  <CDropdown
    class="w-auto flex flex-col gap-2 relative -top-1 max-h-[180px] overflow-y-auto"
    :consistent-menu-width="false"
    :on-clickoutside="handleClick"
    :options="fontOptions"
    placement="bottom-start"
    :render-option="renderDropdownOption"
    :show="showDropdown"
    :themeOverrides="themeOverrides"
    trigger="click"
    @select="handleSelect"
  >
    <CButton :themeOverrides="themeOverrides[selectedOverride]" @click="handleClick">
      <CIcon fa-type="fas" icon="font" size="16px" />
      <CIcon icon="chevron-down" size="12px" />
    </CButton>
  </CDropdown>
</template>

<script lang="ts" setup>
import type { Editor } from '@tiptap/vue-3'
import { type StyleValue, h, ref } from 'vue'
import type { DropdownOption } from './CEditorMenu.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CDropdown from '@lib/components/CDropdown/CDropdown.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import { onMounted } from 'vue'
import useCanvasTheme from '@lib/apps/canvas-builder/composables/useCanvasTheme'
import { DEFAULT_FONT } from '@canvas-builder/constants'
import { orderBy, truncate } from 'lodash-es'

interface FontDropdownOption {
  key: string
  label: string
  url?: string
}

interface Props {
  editor: Editor
  themeOverrides: { active: StyleValue; default: StyleValue }
}

const props = defineProps<Props>()

const { customFonts } = useCanvasTheme()
const fontOptions = ref<FontDropdownOption[]>([{ label: DEFAULT_FONT, key: DEFAULT_FONT }])
const showDropdown = ref(false)

const renderDropdownOption = ({ option }: { option: DropdownOption }) =>
  h(
    'div',
    {
      class:
        'flex items-center justify-between uppercase font-500 gap-4 py-1 px-2 h-9 cursor-pointer hover:bg-gray-100 tracking-wide',
      onClick: () => {
        handleSelect(option)
      },
    },
    [
      h('span', { class: 'c-editor-menu__select-label' }, truncate(option.label, { length: 30 })),
      h(
        'span',
        { class: 'c-editor-menu__select-label', style: `font-family: ${option.label}; text-transform: none;` },
        'Aa',
      ),
    ],
  )

const selectedOverride = ref<'default' | 'active'>('default')

const handleClick = () => {
  showDropdown.value = !showDropdown.value
  selectedOverride.value = selectedOverride.value === 'active' ? 'default' : 'active'
}
const handleSelect = (option: FontDropdownOption) => {
  showDropdown.value = false
  selectedOverride.value = 'default'

  props.editor.commands.setFontFamily(option.key)
}

onMounted(async () => {
  // get unique fonts and sort them in alphabetical order
  if (customFonts.value?.length) {
    fontOptions.value = orderBy(
      [
        ...fontOptions.value,
        ...customFonts.value.map((item) => ({
          key: item.name ?? '',
          label: item.name ?? '',
          url: item.content_url ?? '',
        })),
      ],
      ['label'],
    )
  }
})
</script>
